import { useSuspenseQuery } from '@tanstack/react-query'
import { ChevronDownIcon, Loader2Icon } from 'lucide-react'
import { startTransition, Suspense, useCallback, useMemo, useState } from 'react'

import { ButtonLink } from 'slab'
import { getEmployersImportantDates, getGetEmployersImportantDatesQueryKey } from 'utils/web_api'
import { type EmployersImportantDate } from 'utils/web_api.schemas'

import ImportantDate from './ImportantDate'

const DEFAULT_DAYS_TO_FETCH = 30
const ALL_DAYS_TO_FETCH = 365

function UpcomingImportantDatesComponent ({
  dates,
  isLoading,
  isFetching,
  numDaysToFetch,
  setNumDaysToFetch,
}: {
  dates?: EmployersImportantDate[];
  isLoading: boolean;
  isFetching: boolean;
  numDaysToFetch: number;
  setNumDaysToFetch: (numDays: number) => void;
}) {
  const [expandedImportantDateKey, setExpandedImportantDateKey] = useState<
    string | null
  >(null)

  const handleShowMoreDates = () => {
    setNumDaysToFetch(ALL_DAYS_TO_FETCH)
  }

  const isExpanded = numDaysToFetch === ALL_DAYS_TO_FETCH
  const isFetchingAllDates = isExpanded && isFetching
  const hasLoadedAllDates = isExpanded && !isFetching

  return (
    <div className='flex flex-col gap-6'>
      <p className='body text-gray-500'>Upcoming important dates</p>
      <div className='flex flex-col gap-4'>
        {isLoading && (
          <div className='flex flex-col gap-1'>
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className='animate-pulse h-9 w-full bg-gray-100 rounded-md' />
            ))}
          </div>
        )}
        <div className='flex flex-col gap-1 overflow-y-auto max-h-[270px] -mx-4'>
          {dates?.map((importantDate) => {
            const key = `${importantDate.date}-${importantDate.title}`
            return (
              <ImportantDate
                key={key}
                importantDate={importantDate}
                expanded={expandedImportantDateKey === key}
                onHeaderClick={() => expandedImportantDateKey === key ? setExpandedImportantDateKey(null) : setExpandedImportantDateKey(key)}
              />
            )
          })}
        </div>
        {!hasLoadedAllDates && (
          <div className='flex justify-start -ml-4'>
            <ButtonLink
              variant='tertiary-deemphasized'
              onClick={handleShowMoreDates}
              disabled={isFetchingAllDates}
              rightIcon={isFetchingAllDates ? Loader2Icon : ChevronDownIcon}
            >
              Show more dates
            </ButtonLink>
          </div>
        )}
      </div>
    </div>
  )
}

function UpcomingImportantDatesWithData () {
  const [numDaysToFetch, _setNumDaysToFetch] = useState(DEFAULT_DAYS_TO_FETCH)

  const params = useMemo(() => {
    return { num_future_days: numDaysToFetch }
  }, [numDaysToFetch])

  const { data, isLoading, isFetching } = useSuspenseQuery({
    queryKey: getGetEmployersImportantDatesQueryKey(params),
    queryFn: () => getEmployersImportantDates(params),
  })

  const setNumDaysToFetch = useCallback((numDays: number) => {
    startTransition(() => {
      _setNumDaysToFetch(numDays)
    })
  }, [_setNumDaysToFetch])

  return (
    <UpcomingImportantDatesComponent
      dates={data?.important_dates || []}
      isLoading={isLoading}
      isFetching={isFetching}
      numDaysToFetch={numDaysToFetch}
      setNumDaysToFetch={setNumDaysToFetch}
    />
  )
}

export default function UpcomingImportantDates () {
  const fallback = (
    <UpcomingImportantDatesComponent
      isLoading
      isFetching
      numDaysToFetch={DEFAULT_DAYS_TO_FETCH}
      setNumDaysToFetch={() => {}}
    />
  )

  return (
    <Suspense fallback={fallback}>
      <UpcomingImportantDatesWithData />
    </Suspense>
  )
}
